import { cn } from '@/lib/utils'
import { HTMLAttributes, ReactNode } from 'react'
import EventActivityIssuesBadge, {
  EventActivityIssuesBadgeDensity,
} from './EventActivityIssuesBadge'
import { linkedPlanRevealClassName } from './linkedPlanReveal'
import { useDifficulties } from 'app/frontend/base/hooks/useDifficulties'

type Props = {
  activity: wowaudit.EventActivity
  issues: { selectedCount: number; tooManyPlayers: boolean }
  hasLinkedPlanIssues: boolean
  linkedPlanIssueCount: number
  isLinkedPlanDataLoading?: boolean
  isPlanningInfoLoading?: boolean
  subtitle?: ReactNode
  compact?: boolean
  className?: string
  onIconRef?: (node: HTMLDivElement | null) => void
  iconInteractionProps?: HTMLAttributes<HTMLDivElement>
  isIconDraggable?: boolean
  metaPosition?: 'below' | 'right'
  density?: EventActivityIssuesBadgeDensity
}

const EventActivitySummaryOptionContent = ({
  activity,
  issues,
  hasLinkedPlanIssues,
  linkedPlanIssueCount,
  isLinkedPlanDataLoading = false,
  isPlanningInfoLoading = false,
  subtitle,
  compact = false,
  className,
  onIconRef,
  iconInteractionProps,
  isIconDraggable = false,
  metaPosition = 'below',
  density = 'default',
}: Props) => {
  const { hasDifficulties } = useDifficulties()
  const maxRaidSize = activity.maxRaidSize ?? null
  const isAtTargetSize =
    maxRaidSize !== null
      ? issues.selectedCount >= activity.raidSize &&
        issues.selectedCount <= maxRaidSize
      : issues.selectedCount === activity.raidSize
  const countClassName = cn(
    issues.tooManyPlayers
      ? 'text-warning-foreground'
      : isAtTargetSize
      ? 'text-success-foreground'
      : 'text-foreground',
  )
  const isIssueBadgePending = isPlanningInfoLoading || isLinkedPlanDataLoading
  const issueBadge = (
    <span className={linkedPlanRevealClassName(isIssueBadgePending)}>
      <EventActivityIssuesBadge
        activityId={activity.id}
        hasLinkedPlanIssues={hasLinkedPlanIssues}
        linkedPlanIssueCount={linkedPlanIssueCount}
        density={compact ? 'default' : density}
      />
    </span>
  )

  return (
    <div className={cn('min-w-0', className)}>
      <div
        className={cn(
          'flex gap-1',
          compact && 'flex-col gap-0.5',
          metaPosition === 'right' && !compact && 'items-start',
        )}
      >
        <div
          className={cn(
            'w-10 h-10 min-w-10 min-h-10 relative',
            isIconDraggable && 'cursor-grab active:cursor-grabbing',
            compact && 'mx-auto',
          )}
          ref={onIconRef}
          {...iconInteractionProps}
        >
          {activity.encounter?.thumbnailUrl ||
          activity.instance?.thumbnailUrl ||
          activity.instance?.tileUrl ? (
            <img
              className={cn(
                'w-full h-full rounded-md border-2 border-border shrink-0 transition-colors',
                !activity.active && 'grayscale',
              )}
              src={
                activity.encounter?.thumbnailUrl ??
                activity.instance?.thumbnailUrl ??
                activity.instance?.tileUrl ??
                ''
              }
            />
          ) : (
            <div
              className={cn(
                'w-full h-full rounded-md border-2 border-border bg-muted transition-colors',
                !activity.active && 'grayscale',
              )}
            />
          )}

          {hasDifficulties ? (
            <div className='absolute bottom-px right-1 text-sm font-semibold px-1 py-0.5 leading-none rounded-t-[8px] bg-card-nested/70'>
              {activity.difficulty?.[0].toUpperCase() ?? '-'}
            </div>
          ) : null}
        </div>

        <div className='flex min-w-0 flex-col gap-0.5 grow'>
          <div className='flex items-start justify-between gap-1'>
            <span className='block w-full overflow-hidden text-ellipsis whitespace-nowrap'>
              {activity.encounter?.name || activity.instance?.name}
            </span>
          </div>
          <span className='text-muted-foreground text-xs overflow-hidden text-ellipsis whitespace-nowrap'>
            {subtitle ?? activity.instance?.name}
          </span>
        </div>

        {metaPosition === 'right' && !compact ? (
          activity.active ? (
            <div className='ml-auto flex items-center gap-2 h-5 shrink-0'>
              <span className='text-xs font-mono flex items-center gap-0.5'>
                <span className={countClassName}>{issues.selectedCount}</span>
                <span className='text-muted-foreground'>/</span>
                <span>{activity.raidSize}</span>
              </span>

              {issueBadge}
            </div>
          ) : (
            <div className='ml-auto text-xs text-muted-foreground h-5 shrink-0'>
              inactive
            </div>
          )
        ) : null}
      </div>

      {metaPosition === 'right' && !compact ? null : activity.active ? (
        <div
          className={cn(
            'mt-1 flex items-center justify-between gap-2 h-5',
            compact && 'flex-col h-auto gap-0.5',
            !compact && density !== 'default' && 'gap-1',
          )}
        >
          <span
            className={cn(
              'text-xs font-mono flex items-center gap-0.5 whitespace-nowrap',
              !compact && density !== 'default' && 'gap-0',
            )}
          >
            <span className={countClassName}>{issues.selectedCount}</span>
            <span className='text-muted-foreground'>/</span>
            <span>{activity.raidSize}</span>
          </span>

          {issueBadge}
        </div>
      ) : (
        <div
          className={cn(
            'mt-1 text-xs text-muted-foreground',
            compact ? 'h-9.5' : 'h-5',
          )}
        >
          inactive
        </div>
      )}
    </div>
  )
}

export default EventActivitySummaryOptionContent
